import { create } from 'zustand';
import { getBackendUrl } from '../config/environments';
import { persist } from 'zustand/middleware';

/**
 * Authentication and user store using Zustand with persistence
 */
const useAuth = create(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isLoggedIn: false,
      isLoading: false,
      error: null,
      isNewUser: false,

      // Initialize auth state from storage
      initializeAuth: async () => {
        try {
          // Check if user data exists and validate session
          const { user, isLoggedIn } = get();
          
          if (isLoggedIn && user && user.walletAddress) {
            // Verify user still exists in backend
            try {
              const response = await fetch(`${getBackendUrl()}/api/user/${user.walletAddress}`);
              
              if (response.ok) {
                const result = await response.json();
                if (result.success && result.user) {
                  // Update user data from backend
                  const updatedUser = {
                    ...user,
                    creditBalance: result.user.creditBalance || 0,
                    lastConnectedAt: result.user.last_connected_at,
                    // Keep other current data but update from backend
                  };
                  
                  set({ user: updatedUser, isLoggedIn: true, error: null });
                  return { success: true, user: updatedUser };
                }
              }
              
              // If backend verification fails, logout
              set({ user: null, isLoggedIn: false, error: null });
              return { success: false, error: 'Session expired' };
              
            } catch (error) {
              console.warn('Could not verify user session:', error);
              // Keep local session if backend is temporarily unavailable
              return { success: true, user, offline: true };
            }
          }
          
          return { success: false, error: 'No active session' };
        } catch (error) {
          console.error('Error initializing auth:', error);
          return { success: false, error: error.message };
        }
      },

      // Set user data after login
      setUser: (userData) => set({ 
        user: userData,
        isLoggedIn: true,
        error: null
      }),

      // Legacy email signup method removed in wallet-only migration

      // Legacy email signin method removed in wallet-only migration

      // Legacy Google OAuth method removed in wallet-only migration

      // Legacy email verification method removed in wallet-only migration

      // Legacy wallet linking method removed in wallet-only migration

      // Legacy computed properties removed in wallet-only migration
      // All users now authenticate via wallet only

      // Wallet-only authentication login method
      login: async (credentials) => {
        // Special developer access for testing
        if (credentials?.email === 'lord@lord.com' && credentials?.password === 'lord') {
          const userData = {
            id: 'dev-access',
            email: 'developer@ignita.com',
            username: 'DevAccess',
            display_name: 'Developer Access',
            creditBalance: 999,
            isDevAccess: true,
            accountType: 'premium',
            subscriptionTier: 'enterprise'
          };
          
          set({ 
            user: userData,
            isLoggedIn: true, 
            isLoading: false,
            error: null,
            isNewUser: false
          });
          
          return userData;
        }
        
        // Wallet authentication
        if (credentials?.walletAddress) {
          return await get().walletLogin(credentials.walletAddress, credentials.userData || {});
        }
        
        // Legacy XAMAN format support
        if (credentials?.xaman && credentials?.walletAddress) {
          return await get().walletLogin(credentials.walletAddress, credentials.userData || {});
        }
        
        throw new Error('Only XRPL wallet authentication is supported');
      },

      // Dedicated wallet-only login function
      walletLogin: async (walletAddress, userData = {}) => {
        set({ isLoading: true, error: null });
        
        try {
          const response = await fetch(`${getBackendUrl()}/api/auth/wallet-login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              walletAddress,
              userData
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Wallet login failed');
          }

          const result = await response.json();
          
          if (!result.success) {
            throw new Error(result.error || 'Wallet login failed');
          }

          const userProfile = {
            id: result.user.id,
            walletAddress: result.user.wallet_address,
            email: result.user.email,
            username: result.user.username,
            display_name: result.user.display_name,
            firstName: result.user.first_name,
            lastName: result.user.last_name,
            avatarUrl: result.user.avatar_url,
            bio: result.user.bio,
            isEmailVerified: result.user.is_email_verified,
            accountType: result.user.account_type,
            subscriptionTier: result.user.subscription_tier,
            status: result.user.status,
            onboardingCompleted: result.user.onboarding_completed,
            creditBalance: result.user.creditBalance || 0,
            settings: result.user.settings,
            lastConnectedAt: result.user.last_connected_at,
            createdAt: result.user.created_at,
            isXamanWallet: true
          };

          set({
            user: userProfile,
            isLoggedIn: true,
            isLoading: false,
            error: null,
            isNewUser: result.isNewUser || false
          });

          return { success: true, user: userProfile, isNewUser: result.isNewUser };
          
        } catch (error) {
          console.error('Wallet login failed:', error);
          set({ isLoading: false, error: error.message });
          return { success: false, error: error.message };
        }
      },

      // Update user profile
      updateProfile: async (updates) => {
        const { user } = get();
        if (!user || !user.walletAddress) {
          throw new Error('No user logged in');
        }

        set({ isLoading: true, error: null });

        try {
          const response = await fetch(`${getBackendUrl()}/api/user/${user.walletAddress}/profile`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(updates),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Profile update failed');
          }

          const result = await response.json();
          
          if (!result.success) {
            throw new Error(result.error || 'Profile update failed');
          }

          // Update local user state
          const updatedUser = {
            ...user,
            ...updates,
            updatedAt: new Date().toISOString()
          };

          set({
            user: updatedUser,
            isLoading: false,
            error: null
          });

          return updatedUser;
        } catch (error) {
          set({
            isLoading: false,
            error: error.message
          });
          throw error;
        }
      },

      // Complete onboarding
      completeOnboarding: async (onboardingData = {}) => {
        const { user } = get();
        if (!user || !user.walletAddress) {
          throw new Error('No user logged in');
        }

        set({ isLoading: true, error: null });

        try {
          const response = await fetch(`${getBackendUrl()}/api/user/${user.walletAddress}/onboarding`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(onboardingData),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Onboarding completion failed');
          }

          const result = await response.json();
          
          if (!result.success) {
            throw new Error(result.error || 'Onboarding completion failed');
          }

          // Update local user state
          const updatedUser = {
            ...user,
            onboardingCompleted: true,
            creditBalance: result.creditBalance,
            ...onboardingData
          };

          set({
            user: updatedUser,
            isLoading: false,
            error: null,
            isNewUser: false
          });

          return { user: updatedUser, message: result.message };
        } catch (error) {
          set({
            isLoading: false,
            error: error.message
          });
          throw error;
        }
      },

      // Update user settings
      updateSettings: async (settingsUpdate) => {
        const { user } = get();
        if (!user || !user.walletAddress) {
          throw new Error('No user logged in');
        }

        try {
          const response = await fetch(`${getBackendUrl()}/api/user/${user.walletAddress}/settings`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(settingsUpdate),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Settings update failed');
          }

          const result = await response.json();
          
          if (!result.success) {
            throw new Error(result.error || 'Settings update failed');
          }

          // Update local user state
          const updatedUser = {
            ...user,
            settings: {
              ...user.settings,
              ...result.settings
            }
          };

          set({
            user: updatedUser,
            error: null
          });

          return result.settings;
        } catch (error) {
          set({ error: error.message });
          throw error;
        }
      },

      // Refresh user data
      refreshUser: async () => {
        const { user } = get();
        if (!user || !user.walletAddress) {
          return;
        }

        try {
          const response = await fetch(`${getBackendUrl()}/api/user/${user.walletAddress}`);
          
          if (!response.ok) {
            return; // Silently fail for refresh
          }

          const result = await response.json();
          
          if (result.success && result.user) {
            const updatedUser = {
              ...user,
              creditBalance: result.user.creditBalance || user.creditBalance,
              settings: result.user.settings || user.settings,
              // Update other fields as needed
            };

            set({ user: updatedUser });
          }
        } catch (error) {
          console.warn('Failed to refresh user data:', error);
        }
      },

      // Logout method
      logout: () => {
        set({
          user: null,
          isLoggedIn: false,
          isLoading: false,
          error: null,
          isNewUser: false
        });
      },

      // Clear error
      clearError: () => set({ error: null })
    }),
    { name: 'auth' }
  )
);

export default useAuth;